/* eslint-disable react/prop-types */
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Stack,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ExpandMore as ExpandMoreIcon,
  AccountTree as AccountTreeIcon,
  Edit as EditIcon,
  ReportGmailerrorred as ReportGmailerrorredIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import HazardAccordion from "./HazardAccordion";

function MobileHazardsView({
  workflowEntries,
  expandedHazard,
  onHazardToggle,
  onEditHazard,
  onUnlinkHazard,
  onDeleteHazard,
  onEditWorkflow,
  getRiskLevelColor,
  onUnlinkPrecaution,
  deletingPrecaution,
  onAddPrecaution,
  onAddPrecautionWithCategory,
  isLoading,
  hasError,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {t("hazards.loadingHazards")}
        </Typography>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
        <Box
          sx={{
            display: "inline-flex",
            p: 3,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.error.main, 0.1),
            mb: 2,
          }}
        >
          <ReportGmailerrorredIcon
            sx={{
              fontSize: 40,
              color: "error.main",
              opacity: 0.7,
            }}
          />
        </Box>
        <Typography variant="body1" color="text.secondary" fontWeight="500">
          {t("hazards.errorTitle")}
        </Typography>
      </Box>
    );
  }

  if (workflowEntries.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Box
          sx={{
            display: "inline-flex",
            p: 3,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            mb: 3,
          }}
        >
          <ReportGmailerrorredIcon
            sx={{
              fontSize: 48,
              color: "warning.main",
              opacity: 0.7,
            }}
          />
        </Box>
        <Typography
          variant="h6"
          color="text.secondary"
          gutterBottom
          fontWeight="500"
        >
          {t("hazards.noHazards")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("hazards.noHazardsDesc")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {workflowEntries.map(([workflowId, group]) => (
        <Accordion
          key={workflowId}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: "0 0 16px 0",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderRadius: "8px 8px 0 0",
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ flex: 1, mr: 1 }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: "2px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  width: 40,
                  height: 40,
                }}
              >
                <AccountTreeIcon
                  sx={{
                    color: "primary.main",
                    fontSize: 20,
                  }}
                />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="600">
                  {group.workflow.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.hazards.length} {t("hazards.hazardsInGroup")}
                </Typography>
              </Box>
              {workflowId !== "unassigned" && (
                <Tooltip title={t("worktypeHazards.editTooltip")}>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditWorkflow(group.workflow);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      cursor: "pointer",
                      color: "primary.main",
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        transform: "scale(1.05)",
                      },
                      transition: "all 0.3s ease",
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={t("worktypeHazards.editButtonAria", {
                      name: group.workflow.name,
                    })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        onEditWorkflow(group.workflow);
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            {group.hazards.length > 0 ? (
              group.hazards.map((hazard) => (
                <HazardAccordion
                  key={hazard.id}
                  hazard={hazard}
                  isExpanded={expandedHazard === hazard.id}
                  onToggle={(expanded) =>
                    onHazardToggle(expanded ? hazard.id : null)
                  }
                  onEdit={() => onEditHazard(hazard)}
                  onUnlink={() => onUnlinkHazard(workflowId, hazard)}
                  onDelete={() => onDeleteHazard(hazard.id)}
                  showUnlinkButton={workflowId !== "unassigned"}
                  getRiskLevelColor={getRiskLevelColor}
                  onUnlinkPrecaution={onUnlinkPrecaution}
                  deletingPrecaution={deletingPrecaution}
                  onAddPrecaution={onAddPrecaution}
                  onAddPrecautionWithCategory={onAddPrecautionWithCategory}
                />
              ))
            ) : (
              <Box sx={{ textAlign: "center", py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("hazards.noHazardsInWorkflow")}
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default MobileHazardsView;
